import { computed, map } from 'nanostores';
import { getSupabaseSecrets, createSupabaseSecrets, deleteSupabaseSecrets } from '~/lib/.client/api/supabase';
import { projectStore } from '~/lib/stores/project';
import { hmrPropertyFactory } from '~/utils/hmr';
import { invariant } from '~/utils/invariant';

export interface SecretResponse {
  name: string;
  value: string;
}

export interface SecretCreateRequest {
  name: string;
  value: string;
}

export interface SecretsState {
  secrets: SecretResponse[];
  loading: boolean;
  error: string | null;
}

const hmr = hmrPropertyFactory('SecretsStore');

/**
 * Global store for managing project secrets and their integration with edge functions.
 */
export class SecretsStore {
  @hmr()
  #state = map<SecretsState>({
    secrets: [],
    loading: false,
    error: null,
  });

  // computed getters for easy access
  secrets = computed(this.#state, (state) => state.secrets);
  loading = computed(this.#state, (state) => state.loading);
  error = computed(this.#state, (state) => state.error);

  constructor() {
    // initialize empty state
    this.#updateState({ loading: false, error: null });
  }

  #updateState(updates: Partial<SecretsState>) {
    this.#state.setKey('secrets', updates.secrets ?? this.#state.get().secrets);
    this.#state.setKey('loading', updates.loading ?? this.#state.get().loading);
    this.#state.setKey('error', updates.error ?? this.#state.get().error);
  }

  // fetch all secrets for the current project
  async fetchSecrets(): Promise<SecretResponse[] | undefined> {
    const projectId = projectStore.projectId.get();

    if (!projectId) {
      this.#updateState({ error: 'No project selected', loading: false });
      return undefined;
    }

    this.#updateState({ loading: true, error: null });

    try {
      const secrets = await getSupabaseSecrets(projectId);

      this.#updateState({
        secrets,
        loading: false,
        error: null,
      });

      return secrets;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch secrets';
      this.#updateState({
        loading: false,
        error: errorMessage,
      });
      throw error;
    }
  }

  // create new secrets
  async createSecrets(secrets: SecretCreateRequest[]) {
    const projectId = projectStore.projectId.get();
    invariant(projectId, 'No project selected');

    this.#updateState({ loading: true, error: null });

    try {
      await createSupabaseSecrets(projectId, secrets);

      // refetch secrets to get updated list
      await this.fetchSecrets();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create secrets';
      this.#updateState({
        loading: false,
        error: errorMessage,
      });
      throw error;
    }
  }

  // delete secrets by name
  async deleteSecrets(secretNames: string[]) {
    const projectId = projectStore.projectId.get();
    invariant(projectId, 'No project selected');

    this.#updateState({ loading: true, error: null });

    try {
      const result = await deleteSupabaseSecrets(projectId, secretNames);

      // refetch secrets to get updated list
      await this.fetchSecrets();

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete secrets';
      this.#updateState({
        loading: false,
        error: errorMessage,
      });
      throw error;
    }
  }

  // clear all state
  clear() {
    this.#updateState({
      secrets: [],
      loading: false,
      error: null,
    });
  }
}

export const secretsStore = new SecretsStore();
